import { cn } from '@/lib/utils/cn';

/**
 * Progress of one processing stage (T-0159): a bar and the line under it.
 *
 * The look is the upload progress primitive's (`upload-progress.tsx`,
 * prototype `.upload-progress` in `wizard.css`): a rounded track, amber
 * while work is under way, healthy once done, the AI ink on an AI surface.
 *
 * The bar is filled to `percent`, a whole percent the caller computed from a
 * real count (pages read, passages stored, sections analysed). When the
 * amount of work is not known, `percent` is null: the track pulses and is
 * never filled, and the progressbar carries no value — nothing is invented.
 */

export type StageProgressTone = 'working' | 'done' | 'ai' | 'critical';

export interface StageProgressBarProps {
  /** Whole percent 0–100 of a real count, or null when the amount of work is not known. */
  percent: number | null;
  /** The visible line, e.g. "Reading 40% · 52 of 128 pages". */
  label: string;
  /** Accessible name of the bar, e.g. "Reading SOW.pdf". */
  ariaLabel: string;
  tone?: StageProgressTone;
  /** `sm` is the 4px track of list rows; `md` the 6px track of panels. */
  size?: 'sm' | 'md';
  /** Hide the line (the caller shows it elsewhere). */
  hideLabel?: boolean;
  className?: string;
}

const FILL: Record<StageProgressTone, string> = {
  working: 'bg-accent-amber-600',
  done: 'bg-posture-healthy',
  ai: 'bg-ai-ink',
  critical: 'bg-posture-critical',
};

const TRACK: Record<StageProgressTone, string> = {
  working: 'bg-ink-100',
  done: 'bg-ink-100',
  ai: 'bg-ai-surface-border',
  critical: 'bg-ink-100',
};

export function StageProgressBar({
  percent,
  label,
  ariaLabel,
  tone = 'working',
  size = 'sm',
  hideLabel = false,
  className,
}: StageProgressBarProps) {
  const known = percent !== null && Number.isFinite(percent);
  const value = known ? Math.min(100, Math.max(0, Math.floor(percent))) : null;
  return (
    <div className={cn('w-full', className)}>
      <div
        role="progressbar"
        aria-label={ariaLabel}
        aria-valuemin={0}
        aria-valuemax={100}
        {...(value !== null ? { 'aria-valuenow': value } : {})}
        aria-valuetext={label}
        className={cn(
          'w-full overflow-hidden rounded-full',
          size === 'sm' ? 'h-1' : 'h-1.5',
          TRACK[tone],
          value === null && 'motion-safe:animate-pulse',
        )}
      >
        {value !== null ? (
          <span
            className={cn('block h-full rounded-full', FILL[tone])}
            style={{ width: `${value}%` }}
          />
        ) : null}
      </div>
      {hideLabel ? null : (
        <div
          className={cn(
            'mt-1 text-meta font-medium',
            tone === 'ai' ? 'text-ai-ink' : 'text-ink-700',
          )}
        >
          {label}
        </div>
      )}
    </div>
  );
}
